import { useState, useRef } from 'react'
import { message } from 'antd'
import _ from 'lodash'
import {
  generateVideoDeepAI,
  generateLumaVideo,
  getTaskStatus,
  assembleScenesWithAudio
} from '../../../../../services/api/aiVideoGeneration'

const getFormattedPrompt = (prompt, formData = {}) => {
  const selectedCharacters = formData?.selectedCharacter || []
  const formattedPrompt = selectedCharacters.reduce((acc, char, index) => {
    const placeholder = `__character${index + 1}__`
    return acc.replace(
      new RegExp(placeholder, 'g'),
      char.current.replace(/__/g, '')
    )
  }, prompt)
  return formattedPrompt
}

const getLumaPayload = (videoData, negative_prompt = '', formData) => {
  return {
    prompt: getFormattedPrompt(videoData.prompt, formData),
    img_url: videoData.image_url,
    region: 'singapore',
    model: 'wan2.5-i2v-preview',
    resolution: '1080p',
    duration: videoData.duration,
    audio: true,
    negative_prompt: negative_prompt,
    prompt_extend: true,
    watermark: false,
    seed: Math.floor(Math.random() * 10000),
    s3_folder: 'ai_generated_videos',
    timeout: 600,
    poll_interval: 15,
    video_id: videoData.video_id
  }
}

export const useVideoGeneration = ({
  scenes,
  setScenes,
  formData,
  videoProvider,
  videoId
}) => {
  const [videoGenerating, setVideoGenerating] = useState([])
  const [
    generatingVideoForAllScenes,
    setGeneratingVideoForAllScenes
  ] = useState(false)

  const updateShotDataRef = useRef(
    _.debounce((sceneId, shotId, updatesOrKey, value) => {
      setScenes(prevScenes =>
        prevScenes.map(scene => {
          if (scene.id === sceneId) {
            return {
              ...scene,
              shots: scene.shots.map(shot => {
                if (shot.id === shotId) {
                  if (
                    typeof updatesOrKey === 'object' &&
                    updatesOrKey !== null
                  ) {
                    return { ...shot, ...updatesOrKey }
                  }
                  return { ...shot, [updatesOrKey]: value }
                }
                return shot
              })
            }
          }
          return scene
        })
      )
    }, 500)
  )

  const waitForVideoTaskSuccess = async (taskId, onProgress) => {
    try {
      if (!taskId) return null
      const delayMs = 10000

      while (true) {
        try {
          const statusResponse = await getTaskStatus(taskId)
          const data = statusResponse?.data || null
          const status = data?.status || null

          if (data?.progress && onProgress) {
            onProgress(data.progress)
          }

          if (status === 'success') {
            return data?.output || ''
          }
          if (status === 'failed' || status === 'error') {
            return null
          }
          if (
            status === 'pending' &&
            (data?.progress === '100%' || data?.progress === 100)
          ) {
            continue
          }
        } catch (err) {
          console.error('Error fetching video task status, retrying:', err)
          return null
        }
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
    } catch (err) {
      return null
    }
  }

  const handleRegenerateVideo = async (sceneId, shotId) => {
    try {
      setVideoGenerating(prevStatus => [
        ...(prevStatus || []),
        { sceneId, shotId }
      ])

      const scene = scenes.find(s => s.id === sceneId)
      const shot = scene.shots?.find(sh => sh.id === shotId)

      setScenes(prevScenes =>
        prevScenes.map(s => {
          if (s.id === sceneId) {
            return {
              ...s,
              shots: s.shots.map(sh =>
                sh.id === shotId ? { ...sh, progress: 0 } : sh
              )
            }
          }
          return s
        })
      )

      const videoData = {
        prompt: shot.video_prompt,
        image_url: shot.imageSrc,
        ...(videoProvider !== 'DeepAI' && { duration: shot.video_duration }),
        video_id: videoId
      }

      const request =
        videoProvider === 'DeepAI' ? generateVideoDeepAI : generateLumaVideo
      const payload =
        videoProvider === 'DeepAI'
          ? videoData
          : getLumaPayload(videoData, shot.negative_prompt, formData)
      const response = await request(payload)
      const taskId = response?.data?.task_id
      const videoLink = await waitForVideoTaskSuccess(taskId, progress => {
        const progressVal = parseInt(progress) || 0
        updateShotDataRef.current(sceneId, shotId, 'progress', progressVal)
      })

      setScenes(prevScenes =>
        prevScenes.map(scene => {
          if (scene.id === sceneId) {
            return {
              ...scene,
              shots: scene.shots.map(shot =>
                shot.id === shotId
                  ? {
                      ...shot,
                      progress: 100,
                      ...(videoLink
                        ? {
                            vidSrc: videoLink,
                            errorGeneratingVideo: false,
                            generated_video_duration: shot.video_duration,
                            videos_history: [
                              ...(shot.videos_history || []),
                              {
                                url: videoLink,
                                timestamp: Date.now(),
                                duration_at_generation: shot.video_duration,
                                source_image: shot.imageSrc
                              }
                            ]
                          }
                        : { errorGeneratingVideo: true })
                    }
                  : shot
              )
            }
          }
          return scene
        })
      )

      if (videoLink) {
        message.success(`Video generated successfully!`)
      } else {
        throw new Error('Video generation failed')
      }
    } catch (error) {
      message.error('Error regenerating video: ' + error.message)
      console.error('Error regenerating video:', error)
    } finally {
      setVideoGenerating(prevStatus =>
        (prevStatus || []).filter(
          status => !(status.sceneId === sceneId && status.shotId === shotId)
        )
      )
    }
  }

  const handleGenerateFullVideo = async (targetScenes = null) => {
    try {
      if (!targetScenes) {
        setGeneratingVideoForAllScenes(true)
      }

      const scenesToProcess = targetScenes || scenes
      const affectedSceneIds = new Set()

      const videoQueue = scenesToProcess.flatMap(scene =>
        scene.shots.map(shot => {
          affectedSceneIds.add(scene.id)
          return { scene, shot }
        })
      )

      const promises = videoQueue.map(async ({ scene, shot }) => {
        try {
          const sceneId = scene.id
          const shotId = shot.id

          setVideoGenerating(prevStatus => [
            ...(prevStatus || []),
            { sceneId, shotId }
          ])

          setScenes(prevScenes =>
            prevScenes.map(s => {
              if (s.id === sceneId) {
                return {
                  ...s,
                  shots: s.shots.map(sh =>
                    sh.id === shotId ? { ...sh, progress: 0 } : sh
                  )
                }
              }
              return s
            })
          )

          const videoData = {
            prompt: getFormattedPrompt(shot.video_prompt, formData),
            image_url: shot.imageSrc,
            ...(videoProvider !== 'DeepAI' && {
              duration: shot.video_duration
            }),
            video_id: videoId
          }

          const request =
            videoProvider === 'DeepAI' ? generateVideoDeepAI : generateLumaVideo
          const payload =
            videoProvider === 'DeepAI'
              ? videoData
              : getLumaPayload(videoData, shot.negative_prompt, formData)

          const response = await request(payload)
          const taskId = response?.data?.task_id
          const videoLink = await waitForVideoTaskSuccess(taskId, progress => {
            const progressVal = parseInt(progress) || 0
            updateShotDataRef.current(sceneId, shotId, 'progress', progressVal)
          })

          setScenes(prevScenes =>
            prevScenes.map(scene => {
              if (scene.id === sceneId) {
                return {
                  ...scene,
                  shots: scene.shots.map(shot =>
                    shot.id === shotId
                      ? {
                          ...shot,
                          progress: 100,
                          ...(videoLink
                            ? {
                                vidSrc: videoLink,
                                errorGeneratingVideo: false,
                                generated_video_duration: shot.video_duration,
                                videos_history: [
                                  ...(shot.videos_history || []),
                                  {
                                    url: videoLink,
                                    timestamp: Date.now(),
                                    duration_at_generation: shot.video_duration,
                                    source_image: shot.imageSrc
                                  }
                                ]
                              }
                            : { errorGeneratingVideo: true })
                        }
                      : shot
                  )
                }
              }
              return scene
            })
          )
        } catch (error) {
          console.error(
            `Error generating video for scene ${scene.id}, shot ${shot.id}:`,
            error
          )
          setScenes(prevScenes =>
            prevScenes.map(s => {
              if (s.id === scene.id) {
                return {
                  ...s,
                  shots: s.shots.map(sh =>
                    sh.id === shot.id
                      ? { ...sh, errorGeneratingVideo: true }
                      : sh
                  )
                }
              }
              return s
            })
          )
        } finally {
          setVideoGenerating(prevStatus =>
            (prevStatus || []).filter(
              status =>
                !(status.sceneId === scene.id && status.shotId === shot.id)
            )
          )
        }
      })

      await Promise.all(promises)
      message.success('Video generation process completed.')
    } catch (error) {
      console.error('Error starting video generation:', error)
      message.error('Error starting video generation: ' + error.message)
    } finally {
      setGeneratingVideoForAllScenes(false)
    }
  }

  const handleRetryFailedVideos = () => {
    const scenesWithFailures = scenes
      .map(scene => {
        const failedShots = scene.shots.filter(shot => {
          const isInQueue = videoGenerating.some(
            v => v.sceneId === scene.id && v.shotId === shot.id
          )
          if (isInQueue) return false
          return (
            shot.errorGeneratingVideo || (!shot.vidSrc && !shot.isGenerating)
          )
        })
        if (failedShots.length > 0) {
          return {
            ...scene,
            shots: failedShots
          }
        }
        return null
      })
      .filter(s => s !== null)

    if (scenesWithFailures.length === 0) {
      message.info('No failed videos to retry.')
      return
    }

    handleGenerateFullVideo(scenesWithFailures)
  }

  return {
    videoGenerating,
    generatingVideoForAllScenes,
    handleGenerateFullVideo,
    handleRegenerateVideo,
    handleRetryFailedVideos,
    updateShotDataRef
  }
}
